import React from 'react';
import classNames from 'classnames';

import { noticeEffectiveDateText, releaseDateText } from '../../config/releaseDate';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { ExternalLink } from '../../components';

import css from './marketingPage.module.css';
import legalCss from './LegalDocument.module.css';

const EMAIL_PATTERN = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g;

/**
 * One translated string, with any e-mail address in it made a mailto link.
 * The legal texts write addresses inline ("sendes til post@zuprize.no."), so
 * they are found in the text rather than passed in as values.
 */
const Text = ({ id }) => {
  const intl = useIntl();
  // `{equalTreatment}` in a text is the shared sorting sentence ("Alle
  // selgere behandles likt…"), one key used by Terms §15 and the FAQ.
  const text = intl.formatMessage(
    { id },
    { equalTreatment: intl.formatMessage({ id: 'Sorting.equalTreatment' }) }
  );
  const parts = text.split(EMAIL_PATTERN);
  if (parts.length === 1) {
    return text;
  }
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <ExternalLink key={i} href={`mailto:${part}`} className={css.kontaktLink}>
        {part}
      </ExternalLink>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return (
        <p className={css.body}>
          <Text id={block.id} />
        </p>
      );
    case 'sub':
      return (
        <h3 className={css.subHeading}>
          <Text id={block.id} />
        </h3>
      );
    case 'list':
      return (
        <ul className={css.list}>
          {block.items.map(id => (
            <li key={id}>
              <Text id={id} />
            </li>
          ))}
        </ul>
      );
    case 'lines':
      // An address or contact block: one line each, as in the document.
      return (
        <p className={classNames(css.body, legalCss.lines)}>
          {block.lines.map((id, i) => (
            <React.Fragment key={id}>
              {i > 0 ? <br /> : null}
              <Text id={id} />
            </React.Fragment>
          ))}
        </p>
      );
    case 'table':
      return (
        <div className={legalCss.tableWrapper}>
          <table className={legalCss.table}>
            <thead>
              <tr>
                {block.head.map(id => (
                  <th key={id} scope="col">
                    <Text id={id} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map(row => (
                <tr key={row[0]}>
                  {row.map(id => (
                    <td key={id}>
                      <Text id={id} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

/**
 * A legal document page body (privacy policy, user terms), rendered from a
 * structure generated from the client's own document: headings, paragraphs,
 * lists, tables and address blocks, each a translation key whose text is the
 * document's, verbatim. See privacyPolicyDocument.js and userTermsDocument.js.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.document - `{ header, summary?: { heading, items }, sections }`
 * @param {string} [props.tocLabelId] - translation key for a table of contents; none when omitted
 * @returns {JSX.Element}
 */
const LegalDocument = props => {
  const { document, tocLabelId } = props;
  const { header, summary, sections } = document;
  const intl = useIntl();
  // The release date, set once in src/config/releaseDate.js.
  const releaseDateLabel =
    releaseDateText() || intl.formatMessage({ id: 'LegalDocument.releaseDateUnset' });
  // When the terms apply to existing members: the notice date, release + 30
  // days (Guro, 14 October: "… for nye brukere og fra … for eksisterende").
  const noticeDateLabel =
    noticeEffectiveDateText() || intl.formatMessage({ id: 'LegalDocument.releaseDateUnset' });

  return (
    <article className={css.root}>
      <div className={css.content}>
        <header className={css.hero}>
          {header.eyebrow ? (
            <p className={css.heroEyebrow}>
              <FormattedMessage id={header.eyebrow} />
            </p>
          ) : null}
          <h1 className={css.heroHeading}>
            <FormattedMessage id={header.heading} />
          </h1>
          {header.lede.map(id => (
            <p key={id} className={css.lede}>
              <Text id={id} />
            </p>
          ))}
          <p className={css.lastUpdated}>
            <FormattedMessage
              id={header.lastUpdated}
              values={{ date: releaseDateLabel, noticeDate: noticeDateLabel }}
            />
          </p>
        </header>

        {summary ? (
          <aside className={legalCss.summary} aria-labelledby="legalSummary">
            <h2 id="legalSummary" className={legalCss.summaryHeading}>
              <FormattedMessage id={summary.heading} />
            </h2>
            <ul className={legalCss.summaryList}>
              {summary.items.map(id => (
                <li key={id} className={legalCss.summaryItem}>
                  <Text id={id} />
                </li>
              ))}
            </ul>
          </aside>
        ) : null}

        {tocLabelId ? (
          <nav className={legalCss.toc} aria-labelledby="legalToc">
            <p id="legalToc" className={legalCss.tocEyebrow}>
              <FormattedMessage id={tocLabelId} />
            </p>
            <ul className={legalCss.tocList}>
              {sections.map(section => (
                <li key={section.id} className={legalCss.tocItem}>
                  <a href={`#${section.id}`} className={legalCss.tocLink}>
                    <FormattedMessage id={section.heading} />
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        {sections.map(section => (
          <section key={section.id} id={section.id} className={css.section}>
            <h2 className={css.sectionEyebrow}>
              <FormattedMessage id={section.heading} />
            </h2>
            {section.blocks.map((block, i) => (
              <Block key={`${section.id}-${i}`} block={block} />
            ))}
          </section>
        ))}
      </div>
    </article>
  );
};

export default LegalDocument;
